import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, TOKEN_STORAGE_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Search, Download, FileDown, Trash2, ExternalLink, Loader2,
  Award, XCircle, Clock, Bookmark, Landmark,
} from "lucide-react";

// The 13 pipeline stages required by spec, in canonical order.
const STAGES = [
  { key: "available", label: "Available", tone: "#7A8C77" },
  { key: "saved", label: "Saved", tone: "#7A8C77" },
  { key: "preparing", label: "Preparing Application", tone: "#5E5E5E" },
  { key: "application_started", label: "Application Started", tone: "#C1654B" },
  { key: "submitted", label: "Submitted", tone: "#1C3F35" },
  { key: "under_review", label: "Under Review", tone: "#1C3F35" },
  { key: "additional_info_requested", label: "Additional Info Requested", tone: "#C1654B" },
  { key: "interview_scheduled", label: "Interview Scheduled", tone: "#1C3F35" },
  { key: "awarded", label: "Awarded", tone: "#1C3F35" },
  { key: "partially_awarded", label: "Partially Awarded", tone: "#1C3F35" },
  { key: "denied", label: "Denied", tone: "#5E5E5E" },
  { key: "withdrawn", label: "Withdrawn", tone: "#5E5E5E" },
  { key: "closed", label: "Closed", tone: "#5E5E5E" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// Legacy statuses (from before the pipeline refactor) → display label.
const LEGACY_LABELS = { draft: "Draft", in_review: "In review", rejected: "Denied" };

const stageLabel = (key) => STAGE_MAP[key]?.label || LEGACY_LABELS[key] || key;
const stageTone = (key) => STAGE_MAP[key]?.tone || "#5E5E5E";

const fmtMoney = (n) => (n == null || n === 0) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function StatCard({ label, value, hint, icon: Icon, tone }) {
  return (
    <div className="bg-white border border-[#E6E4DF] rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[#5E5E5E]">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: tone || "#1C3F35" }}/>}
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="font-heading text-3xl font-light text-[#1A1A1A] mt-2">{value}</div>
      {hint && <div className="text-xs text-[#5E5E5E] mt-1">{hint}</div>}
    </div>
  );
}

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      params.set("sort", sortKey);
      params.set("order", sortOrder);
      const [appsRes, pipeRes] = await Promise.all([
        api.get(`/applications?${params.toString()}`),
        api.get("/applications/pipeline/stats"),
      ]);
      setApps(appsRes.data.applications || []);
      setPipeline(pipeRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sortKey, sortOrder]);

  const updateStatus = async (id, status) => {
    await api.patch(`/applications/${id}`, { status });
    toast.success(`Status → ${stageLabel(status)}`);
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { id, notes, amount_requested, amount_awarded, status } = editing;
    await api.patch(`/applications/${id}`, { notes, amount_requested, amount_awarded, status });
    toast.success("Application saved");
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/applications/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    // Since axios can't be used for direct file download with auth headers easily,
    // use fetch and trigger download via blob.
    fetch(`${API_BASE}/applications/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Export failed");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "grant_pipeline.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't export CSV"));
  };

  const bs = pipeline?.by_status || {};
  const stageCount = (k) => bs[k]?.count || 0;

  // Client-side text filter (server also does it, but this stays snappy while typing).
  const visibleApps = useMemo(() => {
    if (!q) return apps;
    const needle = q.toLowerCase();
    return apps.filter((a) =>
      (a.grant_title || "").toLowerCase().includes(needle) ||
      (a.grant_agency || "").toLowerCase().includes(needle) ||
      (a.notes || "").toLowerCase().includes(needle)
    );
  }, [apps, q]);

  const renderTableBody = () => {
    if (loading) {
      return (
        <tr><td colSpan={6} className="py-16 text-center text-[#5E5E5E]">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading pipeline…
        </td></tr>
      );
    }
    if (visibleApps.length === 0) {
      return (
        <tr><td colSpan={6} className="py-16 text-center text-[#5E5E5E]">
          No applications match. <Link to="/grants" className="text-[#1C3F35] underline underline-offset-4">Browse grants</Link>
        </td></tr>
      );
    }
    return visibleApps.map((a) => (
      <tr key={a.id} className="border-t border-[#E6E4DF] hover:bg-[#F9F8F6]" data-testid={`pipeline-row-${a.id}`}>
        <td className="py-3 pr-4 min-w-[240px]">
          <div className="font-medium text-[#1A1A1A] line-clamp-1">{a.grant_title}</div>
          <div className="text-xs text-[#5E5E5E] mt-0.5">{a.grant_agency}</div>
        </td>
        <td className="py-3 pr-4 text-sm">
          <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
            <SelectTrigger className="h-8 text-xs bg-white border-[#E6E4DF] w-[210px]" data-testid={`row-status-${a.id}`}>
              <span className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageTone(a.status) }}/>
                {stageLabel(a.status)}
              </span>
            </SelectTrigger>
            <SelectContent>
              {STAGES.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="py-3 pr-4 text-sm text-[#1A1A1A] whitespace-nowrap">{fmtMoney(a.amount_requested)}</td>
        <td className="py-3 pr-4 text-sm text-[#1C3F35] whitespace-nowrap font-medium">{fmtMoney(a.amount_awarded)}</td>
        <td className="py-3 pr-4 text-xs text-[#5E5E5E] whitespace-nowrap">{fmtDate(a.grant_deadline)}</td>
        <td className="py-3 pr-4 text-xs text-[#5E5E5E] whitespace-nowrap">
          <div className="flex items-center gap-1">
            {a.grant_url && (
              <a
                href={a.grant_url} target="_blank" rel="noopener noreferrer"
                data-testid={`row-open-${a.id}`}
                className="p-1.5 rounded text-[#5E5E5E] hover:text-[#1C3F35] hover:bg-[#E6E4DF]/50"
                title="Open external application"
              >
                <ExternalLink className="w-3.5 h-3.5"/>
              </a>
            )}
            <button
              onClick={() => setEditing(a)}
              data-testid={`row-edit-${a.id}`}
              className="px-2 py-1 rounded text-xs text-[#1C3F35] hover:bg-[#1C3F35]/5"
            >Edit</button>
            <button
              onClick={() => remove(a.id)}
              data-testid={`row-delete-${a.id}`}
              className="p-1.5 rounded text-[#5E5E5E] hover:text-[#C1654B] hover:bg-[#C1654B]/10"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5"/>
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="space-y-8" data-testid="pipeline-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Pipeline</div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">Grant Pipeline</h1>
          <p className="text-[#5E5E5E] mt-2">Every grant you've applied to, in one place. Update statuses as you go.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="pipeline-export" className="border-[#E6E4DF]">
            <FileDown className="w-4 h-4 mr-2"/>Export CSV
          </Button>
          <Link to="/grants">
            <Button data-testid="pipeline-browse" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">
              Browse grants
            </Button>
          </Link>
        </div>
      </header>

      {/* Stats grid */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pipeline-stats-grid">
        <StatCard label="Grants Available" value={pipeline?.total_grants_available ?? "—"} icon={Landmark} tone="#7A8C77"/>
        <StatCard label="Saved" value={pipeline?.saved_grants ?? 0} icon={Bookmark} tone="#7A8C77"/>
        <StatCard label="Applications Started" value={stageCount("application_started")} icon={Clock} tone="#C1654B"/>
        <StatCard label="Submitted" value={stageCount("submitted")} icon={Clock} tone="#1C3F35"/>
        <StatCard label="Under Review" value={stageCount("under_review")} icon={Clock} tone="#1C3F35"/>
        <StatCard label="Awarded" value={stageCount("awarded") + stageCount("partially_awarded")} icon={Award} tone="#1C3F35"/>
        <StatCard label="Denied" value={stageCount("denied")} icon={XCircle} tone="#5E5E5E"/>
        <StatCard label="Requested" value={fmtMoney(pipeline?.total_requested)} hint="Across all applications"/>
        <StatCard label="Awarded $" value={fmtMoney(pipeline?.total_awarded)} hint="Total awarded to date" tone="#1C3F35"/>
      </section>

      {/* Recent activity + upcoming deadlines */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="upcoming-deadlines">
          <div className="label-eyebrow mb-3">Upcoming deadlines</div>
          {pipeline?.upcoming_deadlines?.length ? (
            <ul className="divide-y divide-[#E6E4DF]">
              {pipeline.upcoming_deadlines.map((u) => (
                <li key={u.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#1A1A1A] font-medium truncate">{u.grant_title}</div>
                    <div className="text-xs text-[#5E5E5E]">{stageLabel(u.status)}</div>
                  </div>
                  <div className="text-xs text-[#C1654B] font-medium whitespace-nowrap">{fmtDate(u.grant_deadline)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[#5E5E5E]">No upcoming deadlines.</div>
          )}
        </div>

        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="recent-activity">
          <div className="label-eyebrow mb-3">Recent activity</div>
          {pipeline?.recent_activity?.length ? (
            <ul className="divide-y divide-[#E6E4DF]">
              {pipeline.recent_activity.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#1A1A1A] font-medium truncate">{r.grant_title}</div>
                    <div className="text-xs text-[#5E5E5E]">Moved to {stageLabel(r.status)}</div>
                  </div>
                  <div className="text-xs text-[#5E5E5E] whitespace-nowrap">{fmtDate(r.updated_at)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[#5E5E5E]">No activity yet.</div>
          )}
        </div>
      </section>

      {/* Search / filter / sort */}
      <section className="bg-white border border-[#E6E4DF] rounded-lg p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]"/>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, agency, or notes…"
            className="pl-9 bg-white border-[#E6E4DF]"
            data-testid="pipeline-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[220px] bg-white border-[#E6E4DF]" data-testid="pipeline-status-filter">
            <SelectValue placeholder="All statuses"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={`${sortKey}:${sortOrder}`} onValueChange={(v) => { const [k, o] = v.split(":"); setSortKey(k); setSortOrder(o); }}>
          <SelectTrigger className="w-full sm:w-[220px] bg-white border-[#E6E4DF]" data-testid="pipeline-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at:desc">Recently updated</SelectItem>
            <SelectItem value="created_at:desc">Recently added</SelectItem>
            <SelectItem value="grant_deadline:asc">Soonest deadline</SelectItem>
            <SelectItem value="amount_requested:desc">Largest requested</SelectItem>
          </SelectContent>
        </Select>
      </section>

      {/* Table */}
      <section className="bg-white border border-[#E6E4DF] rounded-lg overflow-x-auto" data-testid="pipeline-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#5E5E5E] font-bold">
              <th className="px-4 py-3">Grant</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3">Awarded</th>
              <th className="px-4 py-3">Deadline</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </section>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl bg-white" data-testid="edit-app-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing?.grant_title}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7A8C77] font-bold">Amount requested</label>
                  <Input
                    type="number"
                    value={editing.amount_requested || ""}
                    onChange={(e) => setEditing({ ...editing, amount_requested: e.target.value ? Number(e.target.value) : null })}
                    data-testid="edit-amount"
                    className="mt-1 bg-white border-[#E6E4DF]"
                    placeholder="50000"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#7A8C77] font-bold">Amount awarded</label>
                  <Input
                    type="number"
                    value={editing.amount_awarded || ""}
                    onChange={(e) => setEditing({ ...editing, amount_awarded: e.target.value ? Number(e.target.value) : null })}
                    data-testid="edit-awarded"
                    className="mt-1 bg-white border-[#E6E4DF]"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#7A8C77] font-bold">Status</label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="mt-1 bg-white border-[#E6E4DF]" data-testid="edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#7A8C77] font-bold">Notes</label>
                <Textarea
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="edit-notes"
                  className="mt-1 bg-white border-[#E6E4DF]"
                  rows={4}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel">Cancel</Button>
                <Button onClick={saveEdit} data-testid="edit-save" className="bg-[#1C3F35] text-white hover:bg-[#1A362D]">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
